<template>
  <div>
    <v-toolbar flat>
      <v-col cols="4">
        <v-text-field
          v-model="search"
          prepend-icon="mdi-magnify"
          clearable
          label="Search"
          single-line
          hide-details
        ></v-text-field>
      </v-col>
      <v-col cols="2">
        <v-select
          v-model="status"
          :items="statuses"
          label="Status"
          dense
          hide-details
          clearable
        ></v-select>
      </v-col>
      <v-spacer></v-spacer>
      <new-invoice-batch-modal></new-invoice-batch-modal>
      <export-invoice-batches></export-invoice-batches>
    </v-toolbar>
    <v-data-table
      :headers="headers"
      :items="filteredBatches"
      :search="search"
      :loading="loading"
      sort-by="batchDate"
      sort-desc
      item-key="invoiceBatchId"
    >
      <template v-slot:[`item.status`]="{ item }">
        <v-tooltip v-if="item.invoiceErrorStatus" bottom>
          <template v-slot:activator="{ on }">
            <v-chip v-on="on" color="error" label x-small text-color="white">{{
              item.status
            }}</v-chip>
          </template>
          <span
            >There was an error invoicing this batch. Please contact
            support.</span
          >
        </v-tooltip>
        <v-chip
          v-else
          :color="item.statusColor"
          label
          x-small
          text-color="white"
          >{{ item.status }}</v-chip
        >
      </template>
      <template v-slot:[`item.batchNumber`]="{ item }">
        <invoice-batch-details-modal
          :number="item.batchNumber"
          :date="item.uploadedAt"
          :display="{ number: 'Batch Number', date: 'Batch Uploaded Date' }"
          @save="setBatchNumber(item, ...arguments)"
        ></invoice-batch-details-modal>
      </template>
      <template v-slot:[`item.invoiceNumber`]="{ item }">
        <invoice-batch-details-modal
          :number="item.invoiceNumber"
          :date="item.invoicedAt"
          :display="{
            number: 'Invoice Number',
            date: 'Invoice Submitted Date',
          }"
          :disabled="!Boolean(item.batchNumber)"
          @save="setInvoiceNumber(item, ...arguments)"
        ></invoice-batch-details-modal>
        <confirm-modal ref="confirm"></confirm-modal>
      </template>
      <template v-slot:[`item.voucherNumber`]="{ item }">
        <invoice-batch-details-modal
          :number="item.voucherNumber"
          :date="item.certifiedAt"
          :display="{
            number: 'Voucher Number',
            date: 'Invoice Certified Date',
          }"
          :disabled="!Boolean(item.invoiceNumber) || item.invoiceErrorStatus"
          @save="setVoucherNumber(item, ...arguments)"
        ></invoice-batch-details-modal>
      </template>
      <template v-slot:[`item.actions`]="{ item }">
        <v-row>
          <v-btn
            text
            x-small
            color="primary"
            @click="goToBatch(item, 'ProcessInvoiceBatch')"
            v-if="!Boolean(item.certifiedAt)"
          >
            Process
          </v-btn>
          <v-btn
            text
            x-small
            color="primary"
            @click="goToBatch(item, 'ReviewInvoiceBatch')"
            v-if="Boolean(item.certifiedAt)"
          >
            Review
          </v-btn>
          <export-invoice-batch-button
            :invoiceBatchId="item.invoiceBatchId"
          ></export-invoice-batch-button>
          <v-btn
            text
            x-small
            color="primary"
            @click="deleteInvoiceBatch(item)"
            v-if="!Boolean(item.certifiedAt)"
          >
            Delete
          </v-btn>
          <confirm-modal ref="confirm"></confirm-modal>
        </v-row>
      </template>
    </v-data-table>
  </div>
</template>
<script>
import NewInvoiceBatchModal from "./new-invoice-batch-modal.vue";
import ConfirmModal from "../Shared/confirm-modal.vue";
import InvoiceBatchDetailsModal from "./invoice-batch-details-modal.vue";
import ExportInvoiceBatchButton from "./export-invoice-batch-button.vue";
import { InvoiceBatchStatus } from "../../objs/InvoiceBatchStatus";
import ExportInvoiceBatches from "./export-invoice-batches.vue";
export default {
  components: {
    NewInvoiceBatchModal,
    ConfirmModal,
    InvoiceBatchDetailsModal,
    ExportInvoiceBatchButton,
    ExportInvoiceBatches,
  },
  async mounted() {
    this.loading = true;
    await this.$store.dispatch("billingModule/getAllInvoiceBatches");
    this.loading = false;
  },
  data() {
    return {
      headers: [
        { text: "Batch ID", value: "invoiceBatchId" },
        { text: "Batch Date", value: "formattedBatchDate" },
        { text: "Status", value: "status" },
        {
          text: "Batch Number",
          value: "batchNumber",
          align: "center",
          sortable: false,
        },
        {
          text: "Invoice Number",
          value: "invoiceNumber",
          align: "center",
          sortable: false,
        },
        {
          text: "Voucher Number",
          value: "voucherNumber",
          align: "center",
          sortable: false,
        },
        {
          text: "Total Amount",
          value: "formattedTotalAmount",
          sortable: false,
        },
        { text: "Actions", value: "actions", sortable: false },
      ],
      loading: false,
      search: "",
      status: "",
      statuses: Object.values(InvoiceBatchStatus),
    };
  },
  computed: {
    invoiceBatches() {
      return this.$store.state.billingModule.invoiceBatches || [];
    },
    filteredBatches() {
      if (this.status) {
        return this.invoiceBatches.filter((item) => item.status == this.status);
      }
      return this.invoiceBatches;
    },
  },
  methods: {
    async setBatchNumber(item, number, date) {
      item.batchNumber = number;
      item.uploadedAt = date;
      await this.updateBatch(item);
    },
    async setVoucherNumber(item, number, date) {
      item.voucherNumber = number;
      item.certifiedAt = date;
      await this.updateBatch(item);
    },
    async updateBatch(item) {
      this.$store.commit("uxModule/setShowLoader", true);
      let message = await this.$store.dispatch(
        "billingModule/updateInvoiceBatch",
        { ...item }
      );
      this.$store.commit("uxModule/setShowLoader", false);

      if (message) {
        this.$store.commit("uxModule/setSnackbarMsg", message);
        this.$store.commit("uxModule/setShowSnackbar", true);
      }
    },
    async setInvoiceNumber(item, number, date) {
      if (
        await this.$refs.confirm.open(
          "Confirm",
          "This will mark all sessions in batch as Invoiced. Continue?"
        )
      ) {
        this.$store.commit("uxModule/setShowLoader", true);
        let message = await this.$store.dispatch(
          "billingModule/postInvoiceBatch",
          {
            invoiceBatchId: item.invoiceBatchId,
            invoiceNumber: number,
            invoicedAt: date,
          }
        );
        this.$store.commit("uxModule/setShowLoader", false);

        if (message) {
          this.$store.commit("uxModule/setSnackbarMsg", message);
          this.$store.commit("uxModule/setShowSnackbar", true);
        }
      }
    },
    async goToBatch(item, routeName) {
      this.$store.commit("uxModule/setShowLoader", true);
      await this.$store.dispatch("billingModule/loadInvoiceBatch", {
        invoiceBatchId: item.invoiceBatchId,
      });
      this.$store.commit("uxModule/setShowLoader", false);
      this.$router.push({ name: routeName });
    },
    deleteInvoiceBatch: async function (item) {
      if (
        await this.$refs.confirm.open(
          "Confirm",
          "Are you sure you want to delete this invoice batch and all associated session invoices?"
        )
      ) {
        this.$store.commit("uxModule/setShowLoader", true);
        let message = await this.$store.dispatch(
          "billingModule/deleteInvoiceBatch",
          {
            invoiceBatchId: item.invoiceBatchId,
          }
        );
        this.$store.commit("uxModule/setShowLoader", false);

        if (message) {
          this.$store.commit("uxModule/setSnackbarMsg", message);
          this.$store.commit("uxModule/setShowSnackbar", true);
        }
      }
    },
  },
};
</script>
